import cx from "classnames";
import { useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import BackButton from "./BackButton";

// Keep in sync with the max-width media query in add-exercise-modal.css that
// stacks the aside below .box instead of matching its height side-by-side.
const ASIDE_SIDE_BY_SIDE_MIN_WIDTH = 900;

interface ModalProps {
  children: React.ReactNode;
  toClear?: string[];
  onClose?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  canClose?: boolean;
  backButton?: boolean;
  forwardButton?: boolean;
  goForward?: () => void;
  title?: string;
  headerStart?: React.ReactNode | null;
  className?: string;
  header?: boolean;
  aside?: React.ReactNode | null;
  customTitle?: React.ReactNode | null;
}

const Modal = ({
  children,
  toClear = [],
  onClose = () => {},
  canClose = true,
  backButton = false,
  forwardButton = false,
  goForward = () => {},
  title,
  /** Renders inside the header row, left of the close control (after back button, before title). */
  headerStart = null,
  /** Renders as a sibling panel next to the modal box (e.g. the workout queue). */
  aside = null,
  className,
  header = true,
  customTitle = null,
}: ModalProps) => {
  const { t } = useTranslation("common");
  const boxRef = useRef(null);
  // .box's height hugs its own content (capped at 95%) and can vary with
  // that content (adding a set row, tempo input, etc). Comparing its
  // intrinsic size against the aside's through CSS alone (grid/flex
  // stretch) turned out unreliable in practice — the aside ended up
  // shorter than .box's actual rendered height. Measuring .box directly
  // and applying it as a real pixel height on the aside is unambiguous.
  const [asideHeight, setAsideHeight] = useState(null);

  useLayoutEffect(() => {
    if (!aside || !boxRef.current) return undefined;
    const boxEl = boxRef.current;

    const update = () => {
      const sideBySide = window.innerWidth > ASIDE_SIDE_BY_SIDE_MIN_WIDTH;
      setAsideHeight(sideBySide ? boxEl.offsetHeight : null);
    };
    update();

    window.addEventListener("resize", update);
    let observer: ResizeObserver | undefined;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(update);
      observer.observe(boxEl);
    }
    return () => {
      window.removeEventListener("resize", update);
      observer?.disconnect();
    };
  }, [aside]);

  const toClearString = Array.isArray(toClear)
    ? toClear.join("=&")
    : toClear
      ? [toClear].join("=&")
      : "";

  const closeHref = toClearString
    ? `/actions/refresh?modal=&${toClearString}=`
    : "/actions/refresh?modal=";

  return (
    <div
      role="modal"
      className={cx("Modal-Box", className, { "has-aside": !!aside })}
    >
      <a href={closeHref} onClick={onClose} className="overlay" />

      <div className="box" ref={boxRef}>
        {header && (
          <header>
            <div className="modal-back-container">
              {backButton && <BackButton />}
              {customTitle || <h2 className="capitalize">{title}</h2>}
              {headerStart}
            </div>

            {canClose && (
              <a
                href={closeHref}
                onClick={onClose}
                // className="close"
                role="button"
                data-button-type="tertiary"
                data-icon-only="true"
                aria-label={t("close")}
              >
                <img src="/img/new-icons/red-cross.svg" alt={t("close")} />
              </a>
            )}
          </header>
        )}
        {children}
      </div>
      {aside && (
        <div
          className="modal-aside"
          style={asideHeight ? { height: `${asideHeight}px` } : undefined}
        >
          {aside}
        </div>
      )}
      {forwardButton && (
        <button
          className="modal-nav-btn modal-forward"
          onClick={goForward}
          type="button"
        >
          <img
            className="modal-nav-icon"
            src="/img/svg/angle-right.svg"
            alt="next"
          />
        </button>
      )}
    </div>
  );
};

export default Modal;
